/**
 * DataAgentTable Component
 *
 * Table displaying data agents with actions.
 */

import React from 'react';
import { Button } from '../../../components/ui/Button';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import { DataAgentStatusBadge } from './DataAgentStatusBadge';
import type { DataAgent } from '../../../types/data-agent.types';
import { EyeIcon, PencilIcon, TrashIcon, KeyIcon } from '@heroicons/react/24/outline';

interface DataAgentTableProps {
  dataAgents: DataAgent[];
  isLoading: boolean;
  onView: (dataAgent: DataAgent) => void;
  onEdit: (dataAgent: DataAgent) => void;
  onDelete: (dataAgent: DataAgent) => void;
  onGenerateToken: (dataAgent: DataAgent) => void;
}

export const DataAgentTable: React.FC<DataAgentTableProps> = ({
  dataAgents,
  isLoading,
  onView,
  onEdit,
  onDelete,
  onGenerateToken,
}) => {
  if (isLoading) {
    return <TableSkeleton rows={5} columns={6} />;
  }

  if (dataAgents.length === 0) {
    return (
      <EmptyState
        title="No data agents found"
        description="No data agents found."
      />
    );
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const hasToken = (agent: DataAgent) => {
    return agent.tokenGeneratedAt && !agent.tokenRevokedAt;
  };

  const isTokenExpired = (agent: DataAgent) => {
    if (!agent.tokenExpiresAt) return false;
    return agent.tokenExpiresAt * 1000 < Date.now();
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Agent
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Token
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Last Heartbeat
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Active
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {dataAgents.map((agent) => (
            <tr key={agent.dataAgentId} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {agent.dataAgentName}
                  </div>
                  <div className="text-sm text-gray-500 font-mono">
                    {agent.dataAgentCode}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <DataAgentStatusBadge status={agent.status} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {hasToken(agent) ? (
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      isTokenExpired(agent)
                        ? 'bg-red-100 text-red-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {isTokenExpired(agent) ? 'Expired' : 'Active'}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {agent.tokenRevokedAt ? 'Revoked' : 'No Token'}
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDate(agent.lastHeartbeatAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    agent.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {agent.isActive ? 'Yes' : 'No'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onView(agent)}
                    title="View Details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onGenerateToken(agent)}
                    title="Manage Token"
                  >
                    <KeyIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onEdit(agent)}
                    title="Edit"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => onDelete(agent)}
                    title="Delete"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
